import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Calendar, MapPin, BookmarkSimple, CheckCircle, Plus, X, Sparkle, ArrowUpRight, Gear } from "@phosphor-icons/react";
import { format } from "date-fns";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function ProfilePage() {
  const { t } = useTranslation();
  const { user, loading } = useAuth();
  const [profile, setProfile] = useState(null);
  const [topics, setTopics] = useState([]);
  const [interests, setInterests] = useState([]);
  const [recs, setRecs] = useState([]);
  const [adding, setAdding] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user || !user.role) return;
    Promise.all([api.get("/me/profile"), api.get("/meta/topics"), api.get("/recommendations?limit=4")]).then(([p, tps, rc]) => {
      setProfile(p.data);
      setInterests(p.data.user?.interests || []);
      setTopics(tps.data || []);
      setRecs(rc.data || []);
    }).catch(() => {});
  }, [user]);

  const suggestions = useMemo(() => {
    const set = new Set(interests.map((s) => s.toLowerCase()));
    return topics.filter((t) => !set.has(t.toLowerCase())).slice(0, 18);
  }, [topics, interests]);

  if (loading) return <div className="px-6 md:px-10 lg:px-16 py-16">{t("common.loading")}</div>;
  if (!user || !user.role) {
    return (
      <div data-testid="profile-page" className="px-6 md:px-10 lg:px-16 py-16 max-w-md mx-auto text-center">
        <h1 className="font-display font-bold text-4xl mb-3">{t("common.pleaseSignIn")}</h1>
        <Link to="/login" className="btn-primary">{t("common.signIn")}</Link>
      </div>
    );
  }

  async function saveInterests(next) {
    setBusy(true);
    try {
      const { data } = await api.put("/me/interests", { interests: next });
      setInterests(data.interests || []);
      toast.success(t("profile.interestsSaved"));
    } catch { toast.error(t("settings.saveFailedToast")); }
    finally { setBusy(false); }
  }

  function add(topic) {
    if (!topic || interests.includes(topic)) return;
    saveInterests([...interests, topic]);
  }
  function remove(topic) {
    saveInterests(interests.filter((x) => x !== topic));
  }

  const u = profile?.user || {};
  const saved = profile?.saved || [];
  const attending = profile?.attending || [];
  const memberSince = u.created_at ? format(new Date(u.created_at), "MMMM yyyy") : "—";

  return (
    <div data-testid="profile-page" className="px-6 md:px-10 lg:px-16 py-12 md:py-16 max-w-6xl">
      <div className="overline mb-3">{t("profile.breadcrumb")}</div>

      {/* Hero / identity */}
      <div className="grid md:grid-cols-12 gap-8 items-end border-b border-zinc-950 pb-10">
        <div className="md:col-span-8">
          <h1 className="font-display font-black text-5xl md:text-7xl tracking-[-0.04em] leading-[.92]">
            {u.name || u.email?.split("@")[0]}
          </h1>
          <div className="mt-4 text-zinc-500 font-mono text-sm flex flex-wrap gap-x-6 gap-y-1">
            <span>{u.email}</span>
            <span>·</span>
            <span>{t("profile.memberSince", { date: memberSince })}</span>
            {u.role === "admin" && <span className="text-blue-700 font-semibold">· {t("nav.admin")}</span>}
          </div>
        </div>
        <div className="md:col-span-4 flex md:justify-end gap-2 flex-wrap">
          <Link to="/speakers/me" data-testid="profile-speaker-link" className="btn-accent text-sm" style={{padding:".55rem 1rem"}}><Sparkle size={14} weight="fill" /> {t("profile.speakerProfileCta")}</Link>
          <Link to="/settings" data-testid="profile-settings-link" className="btn-secondary"><Gear size={14} /> {t("nav.settings")}</Link>
        </div>
      </div>

      {/* Interests */}
      <section className="mt-14" data-testid="profile-interests">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="overline mb-1 flex items-center gap-1.5 text-blue-700"><Sparkle size={11} weight="fill" /> {t("profile.interests")}</div>
            <h2 className="font-display font-bold text-2xl tracking-tight">{t("profile.interestsHeading")}</h2>
          </div>
          <span className="font-mono text-xs text-zinc-400">{interests.length}/20</span>
        </div>
        <p className="text-sm text-zinc-600 max-w-2xl mb-5">{t("profile.interestsHelp")}</p>

        <div className="flex flex-wrap gap-2">
          {interests.map((it) => (
            <button key={it} onClick={() => remove(it)} data-testid={`interest-chip-${it}`} disabled={busy} className="group inline-flex items-center gap-1.5 bg-zinc-950 text-white text-sm px-3 py-1.5 hover:bg-zinc-800 transition-colors">
              {it} <X size={12} className="opacity-60 group-hover:opacity-100" />
            </button>
          ))}
          {interests.length === 0 && <div className="text-sm text-zinc-400 font-mono">{t("profile.noInterests")}</div>}
        </div>

        {suggestions.length > 0 && interests.length < 20 && (
          <div className="mt-6">
            <div className="overline mb-2">{t("profile.addInterest")}</div>
            <div className="flex flex-wrap gap-2">
              {suggestions.map((tp) => (
                <button key={tp} onClick={() => add(tp)} data-testid={`interest-add-${tp}`} disabled={busy} className="inline-flex items-center gap-1 border border-zinc-300 text-zinc-700 text-sm px-3 py-1.5 hover:border-zinc-950 hover:bg-zinc-50 transition-colors">
                  <Plus size={11} /> {tp}
                </button>
              ))}
            </div>
            <form onSubmit={(e) => { e.preventDefault(); if (adding.trim()) { add(adding.trim()); setAdding(""); } }} className="mt-3 flex gap-2 max-w-md">
              <input value={adding} onChange={(e) => setAdding(e.target.value)} data-testid="interest-custom-input" placeholder={t("profile.customInterestPlaceholder")} className="input-line text-sm flex-1" />
              <button data-testid="interest-custom-add" disabled={busy || !adding.trim()} className="btn-primary">{t("common.save")}</button>
            </form>
          </div>
        )}
      </section>

      <div className="mt-16 grid md:grid-cols-12 gap-10">
        {/* Saved */}
        <section className="md:col-span-7" data-testid="profile-saved">
          <div className="flex items-end justify-between border-b border-zinc-200 pb-3 mb-5">
            <h2 className="font-display font-bold text-2xl tracking-tight flex items-center gap-2"><BookmarkSimple size={20} weight="duotone" /> {t("profile.saved")}</h2>
            <span className="font-mono text-xs text-zinc-400">{saved.length}</span>
          </div>
          {saved.length === 0 ? (
            <div className="border border-zinc-200 p-10 text-center text-zinc-500 text-sm">
              {t("bookmarksPage.empty")} <Link to="/conferences" className="underline">{t("bookmarksPage.browseLink")}</Link>
            </div>
          ) : (
            <ul className="divide-y divide-zinc-200 border border-zinc-200">
              {saved.map((c) => <ConfRow key={c.id} c={c} testid={`profile-saved-${c.id}`} />)}
            </ul>
          )}
        </section>

        {/* Attending */}
        <section className="md:col-span-5" data-testid="profile-attending">
          <div className="flex items-end justify-between border-b border-zinc-200 pb-3 mb-5">
            <h2 className="font-display font-bold text-2xl tracking-tight flex items-center gap-2"><CheckCircle size={20} weight="duotone" /> {t("profile.attending")}</h2>
            <span className="font-mono text-xs text-zinc-400">{attending.length}</span>
          </div>
          {attending.length === 0 ? (
            <div className="border border-zinc-200 p-10 text-center text-zinc-500 text-sm">{t("profile.notAttending")}</div>
          ) : (
            <ul className="divide-y divide-zinc-200 border border-zinc-200">
              {attending.map((c) => <ConfRow key={c.id} c={c} compact testid={`profile-attending-${c.id}`} />)}
            </ul>
          )}
        </section>
      </div>

      {/* Recommended */}
      {recs.length > 0 && (
        <section className="mt-16" data-testid="profile-recommended">
          <div className="flex items-end justify-between border-b border-zinc-200 pb-3 mb-5">
            <div>
              <div className="overline mb-1">{t("home.forYou")}</div>
              <h2 className="font-display font-bold text-2xl tracking-tight">{t("home.youMightLike")}</h2>
            </div>
            <span className="text-xs font-mono text-zinc-400">{t("home.basedOnBookmarks")}</span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {recs.map((c) => (
              <motion.div key={c.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                <Link to={`/conferences/${c.id}`} data-testid={`profile-rec-${c.id}`} className="block card overflow-hidden group">
                  <div className="aspect-[16/9] bg-zinc-100 overflow-hidden border-b border-zinc-200 bg-grid flex items-center justify-center">
                    <span className="font-display font-black text-4xl text-zinc-300">{c.title[0]}</span>
                  </div>
                  <div className="p-4">
                    <span className="tag">{c.topic}</span>
                    <h3 className="font-display font-bold text-base tracking-tight leading-tight mt-2 group-hover:text-blue-700 line-clamp-2">{c.title}</h3>
                    <div className="mt-2 text-xs text-zinc-500 font-mono">{format(new Date(c.start_date), "MMM d")} · {c.city}</div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function ConfRow({ c, compact, testid }) {
  return (
    <li>
      <Link to={`/conferences/${c.id}`} data-testid={testid} className="block px-5 py-4 hover:bg-[#FAFAFA] group">
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="font-display font-semibold text-base leading-tight group-hover:text-blue-700 transition-colors">{c.title}</div>
            <div className="mt-1 text-xs text-zinc-500 font-mono flex flex-wrap gap-x-3 gap-y-0.5">
              <span className="flex items-center gap-1"><Calendar size={11} /> {format(new Date(c.start_date), "MMM d, yyyy")}</span>
              {!compact && <span className="flex items-center gap-1"><MapPin size={11} /> {c.city}, {c.country}</span>}
              {c.cfp_deadline && <span className="text-blue-700">CFP {format(new Date(c.cfp_deadline), "MMM d")}</span>}
            </div>
          </div>
          <ArrowUpRight size={14} data-arrow="forward" className="text-zinc-300 group-hover:text-blue-700 transition-colors flex-shrink-0" />
        </div>
      </Link>
    </li>
  );
}
